/**
 * Couriers and Hubs, from the fleet console (ADR-0063, M12-2).
 *
 * ## Whole rows, not patches
 *
 * A Courier body and a Hub body carry every editable field, as the geography bodies do. The service
 * compares the body with the stored row and records which fields moved, and a request that moves
 * nothing is refused rather than written: an event claiming an amendment that did not happen is a
 * record nothing can correct (I3).
 *
 * ## A Courier's float is two columns that mean one thing, and it is required
 *
 * `float_limit_minor` is a ceiling in minor units and `float_currency` says which units. **Both are
 * required**, and the service refuses a body missing either as `float_required`: a missing ceiling is
 * not "no ceiling". The assignment guard fails closed on it and dispatch leaves such a Courier out,
 * so a Courier without one can never be given cash-on-delivery work (P2-1, 2026-09-30; the console
 * offered "No ceiling" until then). The schema keeps `null` admissible so the refusal arrives as
 * that domain code rather than as a type error. The amount is a string for I2's reason: an LBP
 * ceiling in minor units passes 2^53. Only the three currencies the platform prices are admitted.
 *
 * ## The reasons are closed
 *
 * For revocation's reason (ADR-0024): free text in an append-only store cannot be corrected or
 * counted. **There is no reason for a lost handset**: that is revocation, which kills a device's
 * sessions, while a suspension only stops new assignment and leaves Legs on the road to finish.
 *
 * ## A Hub is closed and reopened by its own routes
 *
 * Its status is not a field of the Hub body. Closing is refused while the Hub holds cash, and
 * checking that reads every Merchant's ledger, so it is a separate route that pays for the read.
 */
import { z } from 'zod';
import { gpsPinSchema } from './orders.js';
import { renderedText } from './rendered-text.js';
export const COURIER_VEHICLES = ['motorcycle', 'car', 'van', 'foot'];
export const COURIER_STATUSES = ['active', 'suspended', 'inactive'];
export const COURIER_SUSPENSION_REASONS = [
    'conduct',
    'cash_shortfall',
    'documents_expired',
    'courier_request',
    'other',
];
export const COURIER_REACTIVATION_REASONS = ['issue_resolved', 'courier_request', 'suspended_in_error'];
/** The currencies a float ceiling may be set in: the ones the platform prices. */
export const FLOAT_CURRENCIES = ['USD', 'LBP', 'SYP'];
export const HUB_STATUSES = ['active', 'closed'];
export const VEHICLE_STATUSES = ['active', 'retired'];
const name = z.string().trim().min(1).max(200);
const note = z.string().trim().min(1).max(500);
const zoneCode = z.string().trim().min(1).max(64);
const reviewDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'review_date_format');
// Why the unrendered strings below are exempt from the write-time phone check (ADR-0072). A `note`
// lands only in its change event's payload, and no route or view reads one back.
const noteIn = (event) => `stored only in the ${event} event payload; no view renders it`;
const REVIEW_DATE_REASON = 'a date, YYYY-MM-DD by regex, shown re-read from a date column; too few digits in a row for a phone';
const WHOLE_NUMBER_REASON = 'a whole number, digits with no leading zero by regex; shown re-read from a BIGINT column, never as sent';
const INSTANT_REASON = 'a UTC instant by regex; re-serialised with toISOString before it is stored or shown';
const LOCAL_TIME_REASON = 'a wall-clock time, HH:MM or HH:MM:SS by regex; too few digits for a phone';
export const courierSchema = z
    .object({
    firstNameAr: name,
    firstNameEn: name.nullable(),
    vehicle: z.enum(COURIER_VEHICLES),
    zonePool: z.array(zoneCode).min(1).max(50),
    floatLimitMinor: z.string().regex(/^[1-9]\d{0,17}$/u, 'minor_units').nullable(),
    floatCurrency: z.enum(FLOAT_CURRENCIES).nullable(),
    note,
})
    .strict();
// A Zone code must name a Zone, but the fleet list shows the text itself, so it is a reference.
export const courierRendered = renderedText(courierSchema, {
    prose: ['firstNameAr', 'firstNameEn'],
    refs: ['zonePool.*'],
    exempt: { floatLimitMinor: WHOLE_NUMBER_REASON, note: noteIn('courier.registered or courier.amended') },
});
export const suspendCourierSchema = z.object({ reason: z.enum(COURIER_SUSPENSION_REASONS), note }).strict();
export const suspendCourierRendered = renderedText(suspendCourierSchema, {
    exempt: { note: noteIn('courier.suspended') },
});
export const reactivateCourierSchema = z.object({ reason: z.enum(COURIER_REACTIVATION_REASONS), note }).strict();
export const reactivateCourierRendered = renderedText(reactivateCourierSchema, {
    exempt: { note: noteIn('courier.reactivated') },
});
export const hubSchema = z
    .object({
    nameAr: name,
    nameEn: name.nullable(),
    zoneCode,
    landmarkAr: z.string().trim().min(1).max(500).nullable(),
    lat: gpsPinSchema.shape.lat,
    lng: gpsPinSchema.shape.lng,
    owner: name,
    reviewDate,
    note,
})
    .strict();
export const hubRendered = renderedText(hubSchema, {
    prose: ['nameAr', 'nameEn', 'landmarkAr', 'owner'],
    refs: ['zoneCode'],
    exempt: { reviewDate: REVIEW_DATE_REASON, note: noteIn('config.hubs.amended') },
});
export const hubStatusSchema = z.object({ note }).strict();
export const hubStatusRendered = renderedText(hubStatusSchema, {
    exempt: { note: noteIn('config.hubs.amended') },
});
/** A whole number as digits, for a capacity in kilograms or litres. */
const wholeNumber = z.string().regex(/^[1-9]\d{0,8}$/u, 'whole_number');
/**
 * A vehicle as the console states it (ADR-0063, M12-3).
 *
 * Capacity is whole kilograms and whole litres, and the API stores grams and cubic millimetres, the
 * units a declared Parcel is measured in. A blank dimension is `null`, which is no limit. Its type is
 * the Courier vehicle vocabulary, because assigning the vehicle writes that label onto the Courier.
 * Assignment is not a field here: it has its own routes, as a Hub's status does.
 */
export const vehicleSchema = z
    .object({
    registration: z.string().trim().min(1).max(32),
    type: z.enum(COURIER_VEHICLES),
    capacityKg: wholeNumber.nullable(),
    capacityLitres: wholeNumber.nullable(),
    owner: name,
    reviewDate,
    note,
})
    .strict();
export const vehicleRendered = renderedText(vehicleSchema, {
    prose: ['owner'],
    exempt: {
        registration: 'checked by VehiclesService on the normalised value with its own code, registration_phone_shaped (ADR-0071)',
        capacityKg: WHOLE_NUMBER_REASON,
        capacityLitres: WHOLE_NUMBER_REASON,
        reviewDate: REVIEW_DATE_REASON,
        note: noteIn('config.vehicles.amended'),
    },
});
export const assignVehicleSchema = z.object({ courierId: z.string().trim().min(1).max(64), note }).strict();
// The Courier id is echoed back as sent, once it has named a Courier, so it is a reference.
export const assignVehicleRendered = renderedText(assignVehicleSchema, {
    refs: ['courierId'],
    exempt: { note: noteIn('config.vehicles.amended (and courier.amended, when the label moves)') },
});
export const vehicleNoteSchema = z.object({ note }).strict();
export const vehicleNoteRendered = renderedText(vehicleNoteSchema, {
    exempt: { note: noteIn('config.vehicles.amended') },
});
export const COURIER_SHIFT_STATUSES = ['scheduled', 'cancelled'];
/** A Courier's standing against dispatch: no scheduled shifts, one running now, or scheduled but not now. */
export const COURIER_SHIFT_STATES = ['no_shifts', 'on_shift', 'off_shift'];
/** Where one shift stands against the clock. */
export const COURIER_SHIFT_PHASES = ['upcoming', 'current', 'ended', 'cancelled'];
/** UTC with its `Z`, as the FX and catalogue screens take an instant: a zoneless time reads as local. */
const instant = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/u, 'iso_instant');
/**
 * One dated window a Courier works (ADR-0063, M12-4). There is no weekly pattern: a wall-clock rule
 * needs a timezone and a calendar, which are the calendars milestone's.
 */
export const addShiftSchema = z.object({ startsAt: instant, endsAt: instant, note }).strict();
export const addShiftRendered = renderedText(addShiftSchema, {
    exempt: { startsAt: INSTANT_REASON, endsAt: INSTANT_REASON, note: noteIn('config.courier_shifts.amended') },
});
export const cancelShiftSchema = z.object({ note }).strict();
export const cancelShiftRendered = renderedText(cancelShiftSchema, {
    exempt: { note: noteIn('config.courier_shifts.amended') },
});
export const COURIER_SHIFT_PATTERN_STATUSES = ['active', 'cancelled'];
/**
 * A weekly roster (ADR-0065, M15-4), which the dated shift above deliberately is not.
 *
 * **Local, with no `Z`, and that is the whole difference.** A dated shift is an instant and says
 * so; "Mondays 08:00" is a wall-clock rule, and the clock it is read in is the named City
 * Cluster's. Sending it as UTC would make a roster drift an hour twice a year, in a market whose
 * DST rule changed by decree with two days' notice (ADR-0065).
 *
 * `HH:MM` and `HH:MM:SS` are both admitted, as the service-class window admits both, because
 * `<input type="time">` sends the short form and Postgres stores the long one.
 *
 * **ISO 8601 weekdays: 1 = Monday … 7 = Sunday.** Not JavaScript's `getUTCDay`, where 0 is
 * Sunday — migration 0040's CHECK refuses 0 outright, so a JS value reaching the column is a
 * failed INSERT rather than a Sunday rostered as a Monday.
 *
 * **There is no CHECK that the end follows the start**, here or in the database: 22:00 to 06:00
 * is a night shift a delivery platform genuinely has, and the weekday names the day it *starts*
 * on. The dated rows the expansion produces still carry migration 0036's `ends_at > starts_at`,
 * which is where that rule belongs — on an instant, where it is unambiguous.
 */
const localTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(?::[0-5]\d)?$/u, 'local_time_format');
export const addShiftPatternSchema = z
    .object({
    cityClusterId: z.string().trim().min(1).max(64),
    /** ISO 8601: 1 = Monday … 7 = Sunday. */
    weekday: z.number().int().min(1).max(7),
    startsLocal: localTime,
    endsLocal: localTime,
    owner: z.string().trim().min(1).max(200),
    reviewDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/u, 'review_date_format'),
    note,
})
    .strict();
// The City Cluster id is a reference: the roster list shows it, and the schema admits any text.
export const addShiftPatternRendered = renderedText(addShiftPatternSchema, {
    prose: ['owner'],
    refs: ['cityClusterId'],
    exempt: {
        startsLocal: LOCAL_TIME_REASON,
        endsLocal: LOCAL_TIME_REASON,
        reviewDate: REVIEW_DATE_REASON,
        note: noteIn('config.courier_shift_patterns.amended'),
    },
});
/**
 * A pattern is cancelled, never edited — migration 0040 grants UPDATE on its status and its §7
 * pair and nothing else. A roster that changed is a new pattern beside a cancelled one, which is
 * migration 0036's argument about a window that already happened, applied to the rule that
 * produced it.
 */
export const cancelShiftPatternSchema = z.object({ note }).strict();
export const cancelShiftPatternRendered = renderedText(cancelShiftPatternSchema, {
    exempt: { note: noteIn('config.courier_shift_patterns.amended') },
});
//# sourceMappingURL=fleet.js.map